import { Injectable, inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { filter } from 'rxjs/operators';

/** Prevents activate→reload loops if the waiting worker fails to become active. */
const SW_UPDATE_RELOAD_GUARD_KEY = 'sam_ngsw_update_reloaded';

@Injectable({
  providedIn: 'root',
})
export class PwaInstallService {
  private deferredPrompt: any;
  private swUpdate = inject(SwUpdate);

  public installPromptEvent = new BehaviorSubject<boolean>(false);
  public upgradeAvailable = new BehaviorSubject<boolean>(false);

  /** True after the first startup update check finishes (success or failure). */
  private startupSettled = false;
  /** True while we are applying a startup update and about to reload. */
  private applyingStartupUpdate = false;
  /** Last background re-check timestamp (visibility / bfcache resume). */
  private lastBackgroundCheckAt = 0;
  private static readonly BACKGROUND_CHECK_MIN_MS = 5 * 60 * 1000;

  constructor() {
    // Listen for the `beforeinstallprompt` event
    if (typeof window !== 'undefined' && window.localStorage) {
      window.addEventListener('beforeinstallprompt', event => {
        event.preventDefault();
        this.deferredPrompt = event;
        this.installPromptEvent.next(true);
      });
    }

    // Check for updates in the service worker using Angular SwUpdate
    this.checkForUpdates();
  }

  // Trigger the install prompt
  public showInstallPrompt(): void {
    if (this.deferredPrompt) {
      this.deferredPrompt.prompt();
      this.deferredPrompt.userChoice.then((choiceResult: any) => {
        if (choiceResult.outcome === 'accepted') {
          console.log('User accepted the install prompt');
        } else {
          console.log('User dismissed the install prompt');
        }
        this.deferredPrompt = null;
        this.installPromptEvent.next(false);
      });
    }
  }

  public cancelInstallPrompt(): void {
    this.deferredPrompt = null;
    this.installPromptEvent.next(false);
  }

  public activateUpdate(): Promise<boolean> {
    if (this.swUpdate.isEnabled) {
      return this.swUpdate.activateUpdate();
    }
    return Promise.resolve(false);
  }

  private checkForUpdates(): void {
    if (typeof window === 'undefined' || !this.swUpdate.isEnabled) {
      console.log(
        '[PWA Service] Angular SwUpdate is not enabled (e.g. running in dev mode).',
      );
      return;
    }

    console.log(
      '[PWA Service] Angular SwUpdate is enabled. Listening for updates...',
    );

    // After an auto-reload from activateUpdate, skip one more auto-reload cycle.
    let skipAutoReload = false;
    try {
      if (sessionStorage.getItem(SW_UPDATE_RELOAD_GUARD_KEY) === '1') {
        sessionStorage.removeItem(SW_UPDATE_RELOAD_GUARD_KEY);
        skipAutoReload = true;
        console.log(
          '[PWA Service] Startup reload guard cleared; skipping auto-activate this load.',
        );
      }
    } catch {
      // sessionStorage unavailable (private mode edge cases)
    }

    // Mid-session: new version finished downloading → keep existing banner UX.
    // Startup: prefer seamless activate + reload (refresh / PWA reopen).
    this.swUpdate.versionUpdates
      .pipe(
        filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'),
      )
      .subscribe(evt => {
        console.log(
          '[PWA Service] New version detected and ready for installation:',
          evt,
        );

        if (this.applyingStartupUpdate) {
          return;
        }

        if (!this.startupSettled && !skipAutoReload) {
          void this.applyUpdateAndReload('VERSION_READY during startup');
          return;
        }

        this.upgradeAvailable.next(true);
      });

    // A worker that can no longer serve the app.
    //
    // Reported, not acted on. An earlier version of this called
    // location.reload() here, on the reasoning that the worker had already
    // given up on that version so a reload had to come back clean. That
    // reasoning was wrong, and it broke staging:
    //
    //   deploy replaces the hashed chunks -> a client still on the old
    //   manifest asks for one -> 404 -> unrecoverable -> reload -> the SAME
    //   worker is still installed and still on the old version -> 404 again
    //
    // which is an endless reload loop, triggered by exactly the event the
    // handler existed to solve.
    //
    // Recovering properly means removing the broken worker before reloading -
    // unregister it, drop its ngsw: caches, and reload at most once per
    // session so a persistent fault cannot spin. That is worth doing, but it
    // is a considered change and does not belong in a hotfix; logging is
    // enough to make the state visible in the meantime.
    this.swUpdate.unrecoverable.subscribe(event => {
      console.error(
        '[PWA Service] Service worker is in an unrecoverable state:',
        event.reason,
      );
    });

    void this.runStartupUpdateCheck(skipAutoReload);

    // When a mobile PWA / tab is restored from background or bfcache, re-check
    // (throttled). Mid-session: only surface the existing banner — do not force-reload.
    window.addEventListener('pageshow', event => {
      if (!event.persisted) return;
      this.scheduleBackgroundUpdateCheck();
    });

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState !== 'visible') return;
      this.scheduleBackgroundUpdateCheck();
    });
  }

  private scheduleBackgroundUpdateCheck(): void {
    if (!this.startupSettled || !this.swUpdate.isEnabled) return;
    const now = Date.now();
    if (now - this.lastBackgroundCheckAt < PwaInstallService.BACKGROUND_CHECK_MIN_MS) {
      return;
    }
    this.lastBackgroundCheckAt = now;
    void this.swUpdate
      .checkForUpdate()
      .then(hasUpdate => {
        if (hasUpdate) {
          this.upgradeAvailable.next(true);
        }
      })
      .catch(() => {});
  }

  /**
   * On cold start / hard refresh / PWA reopen: if a newer build is waiting,
   * activate it and reload once so users pick up deploys without relying on
   * the banner. Mid-session updates still use the existing banner.
   */
  private async runStartupUpdateCheck(skipAutoReload: boolean): Promise<void> {
    try {
      const hasUpdate = await this.swUpdate.checkForUpdate();
      if (hasUpdate) {
        console.log('[PWA Service] Immediate check: New update available.');
        if (!skipAutoReload) {
          await this.applyUpdateAndReload('startup checkForUpdate');
          return;
        }
        // Guarded reload already ran once — fall back to banner.
        this.upgradeAvailable.next(true);
      } else {
        console.log('[PWA Service] Immediate check: Application is up to date.');
      }
    } catch (err) {
      console.error(
        '[PWA Service] Error checking for updates on startup:',
        err,
      );
    } finally {
      this.startupSettled = true;
    }
  }

  private async applyUpdateAndReload(reason: string): Promise<void> {
    if (this.applyingStartupUpdate || typeof window === 'undefined') {
      return;
    }
    this.applyingStartupUpdate = true;
    console.log(`[PWA Service] Activating update (${reason})…`);

    try {
      const activated = await this.swUpdate.activateUpdate();
      if (!activated) {
        console.warn(
          '[PWA Service] activateUpdate returned false; showing banner instead.',
        );
        this.applyingStartupUpdate = false;
        this.upgradeAvailable.next(true);
        this.startupSettled = true;
        return;
      }

      try {
        sessionStorage.setItem(SW_UPDATE_RELOAD_GUARD_KEY, '1');
      } catch {
        // Still reload; worst case is a single extra check cycle.
      }
      window.location.reload();
    } catch (err) {
      console.error('[PWA Service] Failed to activate update:', err);
      this.applyingStartupUpdate = false;
      this.upgradeAvailable.next(true);
      this.startupSettled = true;
    }
  }
}
